import { useContext, useEffect, useState, useMemo } from "react"
import { Stack } from "@mui/material"
import {
    Button,
    DataTable,
    EmptyContent,
    fetchRoute,
    GenericWrapperContext,
    LoadingContainer,
    TDataTableColumnsData,
    TGenericListReturn,
    FullBar,
    Header,
    Icon,
    TOrder,
    TDataTableRowData,
    AutomationsFactory,
    AutomationModel,
    DialogHandler,
    Dropdown,
    Typography,
    getCalendarFormat,
    Link,
    ListsFactory,
    ListModel,
    Chip
} from "@cakemail-org/ui-components-v2"
import { useTranslation } from "react-i18next"
import { ERoutes } from "../../../utils/url"
import { showDeleteDialog } from "../../../components/dialogs/deleteDialog"
import { showRenameDialog } from "../../../components/dialogs/rename"
import { TAutomationsListing } from "../types"
import { TDropMenuItem } from "@cakemail-org/ui-components-v2/dist/esm/components/dropMenu/types"
import { NameTableCol } from "../../../components/tables/nameTableCol"

export function AutomationsListing({}: TAutomationsListing) {
    const genericWrapper = useContext(GenericWrapperContext)
    const { t } = useTranslation()

    const [loading, setLoading] = useState(false)
    const [order, setOrder] = useState<TOrder>("asc")
    const [orderBy, setOrderBy] = useState<string>("created_on")
    const [page, setPage] = useState<number>(1)
    const [perPage, setPerPage] = useState<number>(25)
    const [automations, setAutomations] = useState<TGenericListReturn<AutomationModel>>()
    const [listNames, setListNames] = useState<Record<number, string>>({})


    useEffect(() => {
        fetchLists()
    }, [])

    useEffect(() => {
        fetchAutomations()
    }, [page, perPage, order, orderBy])


    function fetchAutomations() {
        setLoading(true)
        Promise.all([
            AutomationsFactory.list({
                withCount: true,
                page: page,
                perPage: perPage
            }),
            AutomationsFactory.listWorkflows({
                withCount: true,
                page: page,
                perPage: perPage
            })
        ]).then(([automationsData, workflowsData]) => {
            const allData = [...automationsData.data, ...workflowsData.data]

            const combined = {
                data: allData,
                pagination: {
                    count: (automationsData.pagination?.count || 0) + (workflowsData.pagination?.count || 0),
                    page: page,
                    per_page: perPage
                }
            }
            setAutomations(combined as any)
        }).catch(() => {
            DialogHandler.show({
                isExpress: true,
                type: "error",
                title: t("general-error-action")
            })
        }).finally(() => {
            setLoading(false)
        })
    }

    function fetchLists() {
        return ListsFactory.list({
            withCount: true,
            page: 1,
            perPage: 1000
        }).then((listsData: TGenericListReturn<ListModel>) => {
            setListNames(createListMap(listsData))
        }).catch(() => {
        })
    }

    function handleCreateAutomation() {
        window.location.href = "#" + fetchRoute({ route: ERoutes.createAutomation })
    }

    function createListMap(listsData: TGenericListReturn<ListModel>) {
        return listsData.data.reduce((acc, list) => ({
            ...acc,
            [list.id]: list.name
        }), {} as Record<number, string>)
    }

    function handleSortChange(direction: TOrder, property: keyof TDataTableRowData) {
        setOrder(direction)
        setOrderBy(property as string)
    }

    function handlePaginationChange(cPage: number, cPerPage: number) {
        setPage(cPage)
        setPerPage(cPerPage)
    }

    function renderStatusChip(status: string) {
        const isActive = status === "active"
        return (
            <Chip
                label={isActive ? t("general-on") : t("general-off")}
                color={isActive ? "secondary" : "cloud"}
                size="small"
            />
        )
    }

    function handleViewAutomation(automation: any) {
        window.location.href = "#" + fetchRoute({ route: ERoutes.automationEdit, params: { automationId: automation.id } })
    }

    function handleDeleteAutomation(automation: AutomationModel) {
        if (!automation?.id) {
            DialogHandler.show({
                isExpress: true,
                type: "error",
                title: t("general-error-action")
            })
            return
        }
        showDeleteDialog({
            name: automation.name || "",
            resourceKey: "general-automation_one"
        })
            .then(({ btnRef }) => {
                btnRef.animate()
                return automation.delete()
            })
            .then(() => {
                DialogHandler.show({
                    isExpress: true,
                    type: "success",
                    title: t("general-resource-deleted", { resource: t("general-automation_one") })
                })
                fetchAutomations()
            })
            .catch((e) => {
                if (e) {
                    DialogHandler.show({
                        isExpress: true,
                        type: "error",
                        title: t("general-error-action")
                    })
                }
            })
    }

    function handleRenameAutomation(automation: AutomationModel) {
        showRenameDialog({
            initName: automation.name,
            resourceKey: "general-automation_one"
        })
            .then(({ value, btnRef }) => {
                automation.name = value
                btnRef.animate()
                // use .save instead of .update
                return automation.save()
            })
            .then(() => {
                fetchAutomations()
                DialogHandler.hide()
            })
            .catch((e) => {
                if (e) {
                    DialogHandler.show({
                        isExpress: true,
                        type: "error",
                        title: t("general-error-action")
                    })
                }
            })
    }

    const colsData: TDataTableColumnsData[] = useMemo(() => [
        {
            id: "name",
            label: t("general-user_one"),
            sortable: true
        },
        {
            id: "status",
            label: t("general-status"),
            sortable: true
        },
        {
            id: "list",
            label: t("general-list"),
            sortable: true
        },
        {
            id: "created_on",
            label: t("general-created_on"),
            sortable: true
        },
        {
            id: "updated_on",
            label: t("general-updated_on"),
            sortable: true
        },
        {
            id: "actions",
            label: ""
        }
    ], [])

    const getActionsDropdownOptions = useMemo(() => {
        return (automation: AutomationModel): TDropMenuItem[] => {
            return [
                {
                    isAction: true,
                    text: t("general-rename"),
                    onClick: () => { handleRenameAutomation(automation) }
                },
                {
                    isAction: true,
                    text: t("general-delete"),
                    onClick: () => { handleDeleteAutomation(automation) }
                }
            ]
        }
    }, [])

    const rows: any[] = automations && automations.data.map((automation: AutomationModel, index) => {

        const actions: any = (
            <Stack direction="row" gap={3}>
                <Button
                    variant="outlined"
                    size="small"
                    color="body1"
                    onClick={() => { handleViewAutomation(automation) }}
                >
                    {t("general-open_one")}
                </Button>
                <Dropdown
                    size="medium"
                    color="body1"
                    variant="outlined"
                    iconOnly
                    options={getActionsDropdownOptions(automation)}
                />
            </Stack>
        )

        return {
            id: index,
            name: { id: automation.name, content:<NameTableCol name={automation.name}/> },
            status: { id: automation.status, content: renderStatusChip(automation.status) },
            list: {
                id: automation.list_id,
                content: <Link href={"#" + fetchRoute({ route: ERoutes.contacts }) + "?listId=" + automation?.list_id}>{listNames[automation.list_id] || ""}</Link>
            },
            created_on: {
                id: automation.created_on,
                content: <Typography variant="body1L">{automation.created_on !== 0 ? getCalendarFormat({ unixDate: automation.created_on, showTime: true }) : ""}</Typography>
            },
            updated_on: {
                id: automation.updated_on,
                content: <Typography variant="body1L">{automation.updated_on !== 0 ? getCalendarFormat({ unixDate: automation.updated_on, showTime: true }) : ""}</Typography>
            },
            actions: actions
        }
    }) || []

    return (
        <Stack gap={8}>
            <FullBar
                className={loading ? "loading" : ""}
                left={<Header
                    heading={t("general-automation_other")}
                    headingVariant="titleL"
                    support={genericWrapper.account?.name}
                    headingTooltip={t("")}
                />}
                right={[
                    <Button
                        key="create-automation"
                        variant="contained"
                        size="medium"
                        color="secondary"
                        onClick={handleCreateAutomation}
                        iconOnly
                        disabled={loading}
                    >
                        <Icon name="Add18" />
                    </Button>
                ]}
            />

            <LoadingContainer loading={!automations}>

                {automations && automations?.data?.length === 0 &&
                    <EmptyContent
                        title={t("automations-listing-empty-title")}
                        desc={t("automations-listing-empty-desc")}
                        iconName="CreateForm44"
                        button={ <Button
                            variant="contained"
                            onClick={handleCreateAutomation}
                            size="medium"
                            color="secondary"
                            iconOnly>
                            {`+ ${t('general-add-user')}`}
                        </Button>}
                    />
                }

                {automations && automations.data.length > 0 &&
                    <DataTable
                        initState={{
                            order,
                            orderBy
                        }}
                        totalCount={automations.pagination.count}
                        className={`automationsDataTable ${loading ? "loading" : ""}`}
                        onPaginationChange={handlePaginationChange}
                        // onSortChange={handleSortChange}
                        rows={rows}
                        columnsData={colsData}
                        stickyLastCol
                        showPagination={true}
                    />
                }
            </LoadingContainer>
        </Stack>
    )
}